<template>
  <div class="campaign-detail-page">
    <div class="content-area">
      <div class="page-header">
        <div class="header-left">
          <v-btn icon class="back-btn" aria-label="Back to campaigns" @click="goBack">
            <v-icon size="20">
              mdi-chevron-left
            </v-icon>
          </v-btn>
          <h1 class="page-title">
            {{ campaign ? campaign.name : 'Campaign' }}
          </h1>
          <v-chip v-if="campaign" small :color="status.color" text-color="white" class="ml-3">
            {{ status.label }}
          </v-chip>
        </div>
        <div v-if="campaign && canWrite" class="header-actions">
          <v-btn
            v-if="canStart"
            color="primary"
            class="mank-btn"
            elevation="0"
            :loading="busy === 'start'"
            :disabled="Boolean(busy)"
            @click="runAction('start')">
            <v-icon left small>
              mdi-play
            </v-icon>
            {{ campaign.status === 'draft' ? 'Start' : 'Resume' }}
          </v-btn>
          <v-btn
            v-if="campaign.status === 'active'"
            color="surface-1"
            class="mank-btn"
            elevation="0"
            :loading="busy === 'pause'"
            :disabled="Boolean(busy)"
            @click="runAction('pause')">
            <v-icon left small>
              mdi-pause
            </v-icon>
            Pause
          </v-btn>
          <v-menu offset-y left>
            <template #activator="{ on, attrs }">
              <v-btn icon class="more-btn" v-bind="attrs" :disabled="Boolean(busy)" v-on="on">
                <v-icon>mdi-dots-vertical</v-icon>
              </v-btn>
            </template>
            <v-list dense>
              <v-list-item :disabled="campaign.status === 'cancelled'" @click="openEdit">
                <v-list-item-title>Edit settings</v-list-item-title>
              </v-list-item>
              <v-list-item :disabled="!canCancel" @click="cancelDialog = true">
                <v-list-item-title>Cancel campaign</v-list-item-title>
              </v-list-item>
              <v-list-item :disabled="campaign.status === 'active'" @click="deleteDialog = true">
                <v-list-item-title class="error--text">
                  Delete campaign
                </v-list-item-title>
              </v-list-item>
            </v-list>
          </v-menu>
        </div>
      </div>

      <div class="main-content">
        <div v-if="!campaign && loading" class="loading-state">
          <v-progress-circular indeterminate color="primary" size="48"/>
        </div>

        <template v-else-if="campaign">
          <v-alert
            v-if="campaign.status === 'paused' && campaign.last_error"
            type="warning"
            outlined
            dense
            class="mb-4">
            The campaign was paused automatically: {{ campaign.last_error }}
          </v-alert>
          <v-alert
            v-if="campaign.status === 'draft'"
            type="info"
            outlined
            dense
            class="mb-4">
            This campaign is a draft. Add numbers, then press Start to begin calling.
          </v-alert>

          <div class="summary-grid">
            <div class="summary-card">
              <span class="summary-label">Total numbers</span>
              <span class="summary-value">{{ campaign.total_numbers }}</span>
            </div>
            <div
              v-for="key in numberStatusOrder"
              :key="key"
              class="summary-card summary-card--clickable"
              :class="{ 'summary-card--selected': numberFilter === key }"
              @click="toggleNumberFilter(key)">
              <span class="summary-label">
                <span class="status-dot" :class="`status-dot--${key}`"/>
                {{ numberStatusLabel(key) }}
              </span>
              <span class="summary-value">{{ campaign.number_counts[key] }}</span>
            </div>
          </div>
          <div class="progress-row">
            <v-progress-linear
              :value="progress"
              height="8"
              rounded
              color="success"
              background-color="grey lighten-3"/>
            <span class="text-caption text--secondary">{{ progress }}% finished</span>
          </div>

          <v-row class="mt-2">
            <v-col cols="12" md="5">
              <v-card outlined class="panel-card">
                <h2 class="panel-title">
                  Settings
                </h2>
                <dl class="settings-list">
                  <dt>Agent</dt>
                  <dd>{{ agentName }}</dd>
                  <dt>SIP trunk</dt>
                  <dd><code>{{ campaign.sip_trunk_id }}</code></dd>
                  <dt>Pacing</dt>
                  <dd>{{ campaign.batch_size }} number{{ campaign.batch_size === 1 ? '' : 's' }} every {{ formatSeconds(campaign.interval_seconds) }}</dd>
                  <dt>Attempts</dt>
                  <dd>
                    {{ campaign.max_attempts }}
                    <span v-if="campaign.max_attempts > 1" class="text--secondary">
                      (retry after {{ formatSeconds(campaign.retry_delay_seconds) }})
                    </span>
                  </dd>
                  <dt>Start at</dt>
                  <dd>{{ campaign.start_at ? formatDateTime(campaign.start_at) : 'When started' }}</dd>
                  <dt>End at</dt>
                  <dd>{{ campaign.end_at ? formatDateTime(campaign.end_at) : 'No end time' }}</dd>
                  <dt>Next batch</dt>
                  <dd>{{ nextRunLabel }}</dd>
                  <template v-if="campaign.completed_at">
                    <dt>Completed</dt>
                    <dd>{{ formatDateTime(campaign.completed_at) }}</dd>
                  </template>
                  <dt>Created</dt>
                  <dd>{{ formatDateTime(campaign.created_at) }}</dd>
                </dl>
              </v-card>
            </v-col>

            <v-col cols="12" md="7">
              <v-card outlined class="panel-card">
                <div class="panel-title-row">
                  <h2 class="panel-title">
                    Add numbers
                  </h2>
                  <a href="#" class="template-link" @click.prevent="downloadTemplate">Download CSV template</a>
                </div>
                <p v-if="!canAddNumbers" class="text-body-3 text--secondary mb-0">
                  Numbers cannot be added to a cancelled campaign.
                </p>
                <template v-else>
                  <div class="add-row">
                    <v-file-input
                      :key="fileInputKey"
                      v-model="csvFile"
                      accept=".csv,text/csv"
                      placeholder="Choose a CSV file"
                      outlined
                      dense
                      clearable
                      prepend-icon=""
                      hide-details="auto"
                      :error-messages="csvError"
                      class="mank-input add-input"/>
                    <v-btn
                      color="primary"
                      class="mank-btn"
                      elevation="0"
                      :loading="uploading"
                      :disabled="!csvFile || Boolean(csvError) || uploading"
                      @click="uploadCsv">
                      <Icon name="upload" variant="solid" color="onPrimary" size="18"/>
                      Upload
                    </v-btn>
                  </div>
                  <p class="helper-text">
                    Needs a <code>phone_number</code> column; <code>name</code> and other columns are passed to the agent.
                    Max 5 MB / 10,000 rows.
                  </p>
                  <div class="add-row add-row--top">
                    <MankTextarea
                      v-model="pastedNumbers"
                      rows="2"
                      dense
                      hide-details="auto"
                      class="add-input"
                      placeholder="Or paste numbers: +905551234567, one per line or comma separated"/>
                    <v-btn
                      color="surface-1"
                      class="mank-btn"
                      elevation="0"
                      :loading="addingPasted"
                      :disabled="!pastedNumbers.trim() || addingPasted"
                      @click="addPasted">
                      Add
                    </v-btn>
                  </div>
                  <p v-if="campaign.status === 'completed'" class="helper-text mt-2">
                    This campaign has completed. After adding numbers, press Resume to call them.
                  </p>
                </template>

                <div v-if="lastResult" class="result-box">
                  <div class="result-summary">
                    <span><strong>{{ lastResult.added }}</strong> added</span>
                    <span v-if="lastResult.duplicates"><strong>{{ lastResult.duplicates }}</strong> already in the campaign</span>
                    <span v-if="lastResult.rejected.length" class="error--text">
                      <strong>{{ lastResult.rejected.length }}</strong> rejected
                    </span>
                    <v-spacer/>
                    <v-btn icon x-small aria-label="Dismiss" @click="lastResult = null">
                      <v-icon x-small>
                        mdi-close
                      </v-icon>
                    </v-btn>
                  </div>
                  <v-simple-table v-if="lastResult.rejected.length" dense class="rejected-table">
                    <thead>
                      <tr>
                        <th>Row</th>
                        <th>Value</th>
                        <th>Reason</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr v-for="r in lastResult.rejected.slice(0, 50)" :key="`${r.row}-${r.phone_number}`">
                        <td>{{ r.row }}</td>
                        <td>{{ r.phone_number || '—' }}</td>
                        <td>{{ r.reason }}</td>
                      </tr>
                    </tbody>
                  </v-simple-table>
                  <p v-if="lastResult.rejected.length > 50" class="helper-text mt-2">
                    Showing the first 50 of {{ lastResult.rejected.length }} rejected rows.
                  </p>
                </div>
              </v-card>
            </v-col>
          </v-row>

          <v-card outlined class="panel-card mt-6">
            <div class="panel-title-row">
              <h2 class="panel-title">
                Numbers
              </h2>
              <MankSelect
                v-model="numberFilter"
                :items="numberFilterItems"
                item-text="text"
                item-value="value"
                dense
                hide-details
                class="number-filter"
                :menu-props="{ offsetY: true, nudgeBottom: 8 }"/>
            </div>
            <v-data-table
              :headers="numberHeaders"
              :items="numbers"
              :options.sync="numberOptions"
              :server-items-length="numbersTotal"
              :loading="loadingNumbers"
              :footer-props="{ itemsPerPageOptions: [20, 50, 100] }"
              item-key="id"
              dense
              class="numbers-table"
              no-data-text="No numbers">
              <template #item.name="{ item }">
                {{ item.name || '—' }}
              </template>
              <template #item.status="{ item }">
                <v-chip x-small :color="numberStatusColor(item.status)" text-color="white">
                  {{ numberStatusLabel(item.status) }}
                </v-chip>
                <span v-if="item.status === 'pending' && item.next_attempt_at" class="text-caption text--secondary ml-1">
                  retry {{ formatDateTime(item.next_attempt_at) }}
                </span>
              </template>
              <template #item.attempts="{ item }">
                {{ item.attempts }} / {{ campaign.max_attempts }}
              </template>
              <template #item.last_attempt_at="{ item }">
                {{ formatDateTime(item.called_at || item.last_attempt_at) }}
              </template>
              <template #item.last_error="{ item }">
                <span class="error-cell" :title="item.last_error || ''">{{ item.last_error || '—' }}</span>
              </template>
            </v-data-table>
          </v-card>
        </template>
      </div>
    </div>

    <v-dialog v-model="editDialog" max-width="560">
      <v-card>
        <v-card-title class="text-h6">
          Edit campaign
        </v-card-title>
        <v-card-text>
          <div class="form-field">
            <label class="field-label">Name</label>
            <MankTextField v-model="editForm.name" dense hide-details="auto"/>
          </div>
          <v-row dense>
            <v-col cols="6">
              <div class="form-field">
                <label class="field-label">Numbers per batch</label>
                <MankTextField v-model.number="editForm.batch_size" type="number" dense hide-details="auto"/>
              </div>
            </v-col>
            <v-col cols="6">
              <div class="form-field">
                <label class="field-label">Seconds between batches</label>
                <MankTextField v-model.number="editForm.interval_seconds" type="number" dense hide-details="auto"/>
              </div>
            </v-col>
            <v-col cols="6">
              <div class="form-field">
                <label class="field-label">Attempts per number</label>
                <MankTextField v-model.number="editForm.max_attempts" type="number" dense hide-details="auto"/>
              </div>
            </v-col>
            <v-col cols="6">
              <div class="form-field">
                <label class="field-label">Retry after (seconds)</label>
                <MankTextField v-model.number="editForm.retry_delay_seconds" type="number" dense hide-details="auto"/>
              </div>
            </v-col>
            <v-col cols="6">
              <div class="form-field">
                <label class="field-label">Start at</label>
                <input v-model="editForm.start_at" type="datetime-local" class="native-input">
              </div>
            </v-col>
            <v-col cols="6">
              <div class="form-field">
                <label class="field-label">End at</label>
                <input v-model="editForm.end_at" type="datetime-local" class="native-input">
              </div>
            </v-col>
          </v-row>
          <p class="helper-text">
            Start and end times can be changed but not removed here.
          </p>
        </v-card-text>
        <v-card-actions>
          <v-spacer/>
          <v-btn text :disabled="savingEdit" @click="editDialog = false">
            Cancel
          </v-btn>
          <v-btn
            color="primary"
            class="mank-btn"
            elevation="0"
            :loading="savingEdit"
            @click="saveEdit">
            Save
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="cancelDialog" max-width="440">
      <v-card>
        <v-card-title class="text-h6">
          Cancel this campaign?
        </v-card-title>
        <v-card-text>
          No more numbers will be called and the {{ campaign ? campaign.number_counts.pending : 0 }} pending
          numbers will be marked cancelled. A cancelled campaign cannot be restarted.
        </v-card-text>
        <v-card-actions>
          <v-spacer/>
          <v-btn text :disabled="busy === 'cancel'" @click="cancelDialog = false">
            Keep running
          </v-btn>
          <v-btn
            color="error"
            elevation="0"
            :loading="busy === 'cancel'"
            @click="confirmCancel">
            Cancel campaign
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <DeleteConfirmationModal
      :visible="deleteDialog"
      :confirm-loading="busy === 'delete'"
      :detail="campaign ? `Campaign “${campaign.name}” and its ${campaign.total_numbers} numbers will be removed. This cannot be undone.` : ''"
      @cancel="deleteDialog = false"
      @confirm="confirmDelete"/>
  </div>
</template>

<script>
import DeleteConfirmationModal from '@/components/DeleteConfirmationModal.vue';
import Icon from '@/components/Icon.vue';
import MankSelect from '@/components/MankSelect.vue';
import MankTextField from '@/components/MankTextField.vue';
import MankTextarea from '@/components/MankTextarea.vue';
import AgentService from '@/services/AgentService';
import OutboundCampaignService from '@/services/OutboundCampaignService';
import {
  CAMPAIGN_LIMITS,
  CAMPAIGN_REFRESH_MS,
  NUMBER_STATUS_ORDER,
  campaignStatus,
  downloadCsvTemplate,
  formatDateTime,
  formatSeconds,
  fromDateTimeLocal,
  notifyError,
  notifySuccess,
  numberStatus,
  parsePastedNumbers,
  progressPercent,
  toDateTimeLocal,
} from '@/constants/outboundCampaign';

export default {
  name: 'OutboundCampaignDetail',
  components: {
    DeleteConfirmationModal,
    Icon,
    MankSelect,
    MankTextField,
    MankTextarea,
  },
  data() {
    const initial = this.$route.params.initialResult;
    return {
      campaign: null,
      loading: false,
      agentName: '—',
      busy: '',
      numbers: [],
      numbersTotal: 0,
      loadingNumbers: false,
      numberFilter: '',
      numberOptions: { page: 1, itemsPerPage: 20 },
      numberStatusOrder: NUMBER_STATUS_ORDER,
      csvFile: null,
      fileInputKey: 0,
      uploading: false,
      pastedNumbers: '',
      addingPasted: false,
      // The outcome of the numbers submitted in the create drawer is shown once when arriving from it.
      lastResult: initial && (initial.added || initial.rejected.length) ? initial : null,
      editDialog: false,
      editForm: {},
      savingEdit: false,
      cancelDialog: false,
      deleteDialog: false,
      refreshTimer: null,
      numberHeaders: [
        { text: 'Phone number', value: 'phone_number', sortable: false },
        { text: 'Name', value: 'name', sortable: false },
        { text: 'Status', value: 'status', sortable: false },
        { text: 'Attempts', value: 'attempts', sortable: false, width: '100px' },
        { text: 'Last attempt', value: 'last_attempt_at', sortable: false },
        { text: 'Last error', value: 'last_error', sortable: false },
      ],
    };
  },
  computed: {
    campaignId() {
      return this.$route.params.id;
    },
    canWrite() {
      return this.$store.getters['auth/hasPermission']('write:outbound-campaign');
    },
    status() {
      return campaignStatus(this.campaign?.status);
    },
    progress() {
      return progressPercent(this.campaign);
    },
    canStart() {
      const c = this.campaign;
      return c && ['draft', 'paused', 'completed'].includes(c.status) && c.number_counts.pending > 0;
    },
    canCancel() {
      return this.campaign && ['draft', 'active', 'paused'].includes(this.campaign.status);
    },
    canAddNumbers() {
      return this.canWrite && this.campaign && this.campaign.status !== 'cancelled';
    },
    csvError() {
      if (this.csvFile && this.csvFile.size > CAMPAIGN_LIMITS.csvMaxBytes) return 'File must be at most 5 MB';
      return '';
    },
    numberFilterItems() {
      return [
        { text: 'All statuses', value: '' },
        ...NUMBER_STATUS_ORDER.map((key) => ({ text: numberStatus(key).label, value: key })),
      ];
    },
    nextRunLabel() {
      const c = this.campaign;
      if (!c || c.status !== 'active') return '—';
      if (c.start_at && new Date(c.start_at) > new Date()) return `Starts ${formatDateTime(c.start_at)}`;
      if (!c.next_run_at || new Date(c.next_run_at) <= new Date()) return 'Within a few seconds';
      return formatDateTime(c.next_run_at);
    },
    /** Running campaigns change on their own, so they are refreshed in the background. */
    isLive() {
      const c = this.campaign;
      return c && (c.status === 'active' || c.number_counts.dialing > 0);
    },
  },
  watch: {
    numberOptions(next, prev) {
      if (next.page !== prev.page || next.itemsPerPage !== prev.itemsPerPage) this.loadNumbers();
    },
    numberFilter() {
      if (this.numberOptions.page !== 1) {
        this.numberOptions = { ...this.numberOptions, page: 1 }; // the options watcher reloads
      } else {
        this.loadNumbers();
      }
    },
  },
  async mounted() {
    await Promise.all([this.loadCampaign(), this.loadNumbers()]);
    this.refreshTimer = setInterval(() => {
      if (this.isLive && !document.hidden) this.refresh();
    }, CAMPAIGN_REFRESH_MS);
  },
  beforeDestroy() {
    clearInterval(this.refreshTimer);
  },
  methods: {
    formatDateTime,
    formatSeconds,
    numberStatusLabel(key) {
      return numberStatus(key).label;
    },
    numberStatusColor(key) {
      return numberStatus(key).color;
    },
    goBack() {
      this.$router.push({ name: 'OutboundCampaigns' });
    },
    downloadTemplate() {
      downloadCsvTemplate();
    },
    toggleNumberFilter(key) {
      this.numberFilter = this.numberFilter === key ? '' : key;
    },
    async loadCampaign(options = {}) {
      if (!options.silent) this.loading = true;
      try {
        this.campaign = await OutboundCampaignService.get(this.campaignId, options);
        if (this.agentName === '—') this.loadAgentName();
      } catch (err) {
        if (!options.silent) {
          notifyError(this.$store, err, 'Could not load the campaign.');
          if (err.response?.status === 404) this.goBack();
        }
      } finally {
        this.loading = false;
      }
    },
    async loadAgentName() {
      try {
        const response = await AgentService.getAgentById(this.campaign.agent_id);
        const agent = response?.data || response;
        this.agentName = agent?.name || this.campaign.agent_id;
      } catch (e) {
        this.agentName = this.campaign.agent_id;
      }
    },
    async loadNumbers(options = {}) {
      if (!options.silent) this.loadingNumbers = true;
      try {
        const { page, itemsPerPage } = this.numberOptions;
        const params = { page, page_size: itemsPerPage > 0 ? itemsPerPage : 100 };
        if (this.numberFilter) params.status = this.numberFilter;
        const { items, pagination } = await OutboundCampaignService.listNumbers(this.campaignId, params, options);
        this.numbers = items;
        this.numbersTotal = pagination?.total ?? items.length;
      } catch (err) {
        if (!options.silent) notifyError(this.$store, err, 'Could not load numbers.');
      } finally {
        this.loadingNumbers = false;
      }
    },
    refresh() {
      return Promise.all([this.loadCampaign({ silent: true }), this.loadNumbers({ silent: true })]);
    },
    async runAction(action) {
      this.busy = action;
      try {
        this.campaign = await OutboundCampaignService.action(this.campaignId, action);
        const messages = { start: 'Campaign started.', pause: 'Campaign paused.', cancel: 'Campaign cancelled.' };
        notifySuccess(this.$store, messages[action]);
        await this.loadNumbers({ silent: true });
        return true;
      } catch (err) {
        notifyError(this.$store, err, `Could not ${action} the campaign.`);
        return false;
      } finally {
        this.busy = '';
      }
    },
    async confirmCancel() {
      if (await this.runAction('cancel')) this.cancelDialog = false;
    },
    async confirmDelete() {
      this.busy = 'delete';
      try {
        await OutboundCampaignService.remove(this.campaignId);
        notifySuccess(this.$store, 'Campaign deleted.');
        this.deleteDialog = false;
        this.goBack();
      } catch (err) {
        notifyError(this.$store, err, 'Could not delete the campaign.');
      } finally {
        this.busy = '';
      }
    },
    async afterNumbersAdded(result) {
      this.lastResult = {
        added: result?.added || 0,
        duplicates: result?.duplicates || 0,
        rejected: result?.rejected || [],
      };
      await this.refresh();
    },
    async uploadCsv() {
      if (!this.csvFile || this.csvError) return;
      this.uploading = true;
      try {
        const result = await OutboundCampaignService.uploadNumbersCsv(this.campaignId, this.csvFile);
        this.csvFile = null;
        this.fileInputKey += 1;
        await this.afterNumbersAdded(result);
      } catch (err) {
        notifyError(this.$store, err, 'Could not upload the CSV file.');
      } finally {
        this.uploading = false;
      }
    },
    async addPasted() {
      const numbers = parsePastedNumbers(this.pastedNumbers);
      if (!numbers.length) return;
      this.addingPasted = true;
      try {
        const result = await OutboundCampaignService.addNumbers(this.campaignId, numbers);
        this.pastedNumbers = '';
        await this.afterNumbersAdded(result);
      } catch (err) {
        notifyError(this.$store, err, 'Could not add the numbers.');
      } finally {
        this.addingPasted = false;
      }
    },
    openEdit() {
      const c = this.campaign;
      this.editForm = {
        name: c.name,
        batch_size: c.batch_size,
        interval_seconds: c.interval_seconds,
        max_attempts: c.max_attempts,
        retry_delay_seconds: c.retry_delay_seconds,
        start_at: toDateTimeLocal(c.start_at),
        end_at: toDateTimeLocal(c.end_at),
      };
      this.editDialog = true;
    },
    /** Sends only the fields that changed; the API validates ranges and dates. */
    buildPatch() {
      const c = this.campaign;
      const f = this.editForm;
      const body = {};
      if (f.name.trim() && f.name.trim() !== c.name) body.name = f.name.trim();
      ['batch_size', 'interval_seconds', 'max_attempts', 'retry_delay_seconds'].forEach((key) => {
        if (f[key] !== c[key]) body[key] = f[key];
      });
      ['start_at', 'end_at'].forEach((key) => {
        const next = fromDateTimeLocal(f[key]);
        if (next && f[key] !== toDateTimeLocal(c[key])) body[key] = next;
      });
      return body;
    },
    async saveEdit() {
      const body = this.buildPatch();
      if (!Object.keys(body).length) {
        this.editDialog = false;
        return;
      }
      this.savingEdit = true;
      try {
        this.campaign = await OutboundCampaignService.patch(this.campaignId, body);
        notifySuccess(this.$store, 'Campaign updated.');
        this.editDialog = false;
      } catch (err) {
        notifyError(this.$store, err, 'Could not update the campaign.');
      } finally {
        this.savingEdit = false;
      }
    },
  },
};
</script>

<style lang="scss" scoped>
@import '@/styles/colors.scss';
@import '@/styles/variables.scss';

.campaign-detail-page {
  height: 100%;
}

.header-left {
  display: flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
}

.header-actions {
  display: flex;
  align-items: center;
  gap: 8px;
}

.main-content {
  padding: 24px;
}

.loading-state {
  display: flex;
  justify-content: center;
  padding: 64px;
}

.summary-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(140px, 1fr));
  gap: 12px;
}

.summary-card {
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding: 14px 16px;
  border: 1px solid $light-border;
  border-radius: 12px;
  background: $light-surface-2;

  &.summary-card--clickable {
    cursor: pointer;

    &:hover {
      border-color: $light-foreground-3;
    }
  }

  &.summary-card--selected {
    border-color: $light-primary;
  }
}

.summary-label {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: $text-size-13;
  color: $light-foreground-3;
}

.summary-value {
  font-size: $h5-size;
  font-weight: 700;
  color: $light-foreground-1;
}

.status-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: #9e9e9e;

  &.status-dot--dialing { background: var(--v-info-base); }
  &.status-dot--called { background: var(--v-success-base); }
  &.status-dot--failed { background: var(--v-error-base); }
  &.status-dot--cancelled { background: #607d8b; }
}

.progress-row {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-top: 16px;

  .v-progress-linear {
    flex: 1;
  }
}

.panel-card {
  border-radius: 12px !important;
  padding: 20px 24px;
  height: 100%;
}

.panel-title-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 12px;
}

.panel-title {
  font-size: $body-2-size;
  font-weight: 700;
  color: $light-foreground-1;
  margin: 0 0 12px;

  .panel-title-row & {
    margin: 0;
  }
}

.template-link {
  font-size: $body-3-size;
  color: $light-primary;
  text-decoration: none;
}

.settings-list {
  display: grid;
  grid-template-columns: 120px 1fr;
  row-gap: 10px;
  margin: 0;
  font-size: $body-3-size;

  dt {
    color: $light-foreground-3;
  }

  dd {
    color: $light-foreground-1;
    margin: 0;
    word-break: break-word;
  }
}

.add-row {
  display: flex;
  align-items: center;
  gap: 12px;

  &.add-row--top {
    align-items: flex-start;
    margin-top: 16px;
  }

  .add-input {
    flex: 1;
  }
}

.helper-text {
  font-size: $text-size-13;
  color: $light-foreground-3;
  margin: 6px 0 0;
  line-height: 1.4;
}

.result-box {
  margin-top: 16px;
  padding: 12px;
  border-radius: 8px;
  background: $light-surface-1;
  border: 1px solid $light-border;
}

.result-summary {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 16px;
  font-size: $body-3-size;
}

.rejected-table {
  margin-top: 8px;
  max-height: 240px;
  overflow-y: auto;
  background: transparent !important;
}

.number-filter {
  max-width: 200px;
}

.numbers-table {
  ::v-deep .v-data-table-header th {
    color: $light-foreground-3 !important;
    font-weight: 600;
  }
}

.error-cell {
  display: inline-block;
  max-width: 320px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  vertical-align: middle;
  font-size: $text-size-13;
  color: $light-foreground-3;
}

.form-field {
  display: flex;
  flex-direction: column;
  gap: 6px;
  margin-bottom: 12px;
}

.field-label {
  font-weight: 600;
  font-size: $body-3-size;
  color: $light-foreground-1;
}

.native-input {
  height: 40px;
  padding: 0 12px;
  border-radius: 8px;
  border: 1px solid $light-border;
  background: $light-surface-2;
  color: $light-foreground-1;
  font-family: inherit;
  font-size: $body-3-size;
  width: 100%;

  &:focus {
    outline: none;
    border-color: $light-primary;
  }
}
</style>
